import { makeObservable, computed } from 'mobx'
import { fromStream } from '../utils/mobx-util'
import { Subject, of, Observable } from 'rxjs'
import { map, switchMap, delay, distinctUntilChanged } from 'rxjs/operators'
import { shareBehavior } from '../utils/rx-util'

/**
 * View Model responsible for maintaining search text and exposing
 * a debounced stream that can be used for reactively updating a search query
 * without spamming.
 */
export class SearchTextViewModel {
  /**
   * Stream of changes to the search text and whether to debounce the change
   * for consumers of the stream.
   *
   * @private
   */
  private readonly _changes = new Subject<SearchTextChange>()

  /**
   * Mobx-ified getter for the current search text value, regardless of debounce.
   * @private
   */
  private readonly _value = fromStream(
    '',
    this._changes.pipe(map((x) => x.value)),
  )

  /**
   * The value, debounced accordingly.
   */
  readonly debouncedValue$: Observable<string> = this._changes.pipe(
    switchMap((change) =>
      change.debounced
        ? of(change.value).pipe(delay(this.debounceBy))
        : of(change.value),
    ),
    distinctUntilChanged(),
    shareBehavior(''),
  )

  /**
   * Constructor.
   */
  constructor(private readonly debounceBy = 300) {
    this.change = this.change.bind(this)
    this.clear = this.clear.bind(this)

    makeObservable(this, {
      value: computed,
    })

    // Always keep a subscription to the debounced value.
    // Since it's not referencing anything from the outside,
    // we don't need to dispose the subscription.
    this.debouncedValue$.subscribe()
  }

  /**
   * The search text value.
   */
  get value() {
    return this._value()
  }

  /**
   * Changes the search text.
   *
   * @param newSearchText The new search text.
   * @param immediate Whether to immediately notify consumers. Default is false.
   */
  change(newSearchText: string, immediate = false) {
    this._changes.next({ value: newSearchText, debounced: !immediate })
  }

  /**
   * Clear the search text.
   */
  clear() {
    this.change('', true)
  }
}

/**
 * A change to the search text.
 */
interface SearchTextChange {
  debounced: boolean
  value: string
}
